'use client'

import { useId, useState } from 'react'

import { ArrowBtn } from '@/app/components/atoms/ArrowBtn'
import { VerticalCard } from '@/app/components/shared/cards/VerticalCard'
import { TrackedProductCard } from '@/app/components/shared/TrackedProductCard'
import { withIndex } from '@/app/lib/analytics/mapProductToAnalyticsItem'
import type { ContextualProductListAnalytics } from '@/app/lib/analytics/types'
import { useContextualProductListTracking } from '@/app/lib/analytics/useContextualProductListTracking'
import type { ProductCard } from '@/app/lib/api/mapProductToCard'
import { useDragScroll } from '@/app/lib/hooks/useDragScroll'
import { useRailScroll } from '@/app/lib/hooks/useRailScroll'
import { cx } from '@/app/lib/utils'

import styles from './ProductRail.module.scss'

interface ProductRailProps {
  readonly items: readonly ProductCard[]
  /**
   * `true` ⇒ en móvil/tablet solo se ven las 4 primeras tarjetas hasta que se
   * pulsa el botón "Ver más" (el diseño de referencia). Se activa siempre que
   * haya más de 4 tarjetas, sin relación con el enlace de cabecera del módulo:
   * el botón "Ver más" es una acción interna, no un destino externo (ver
   * `ProductosBotonCompra.tsx`).
   */
  readonly capOnMobile?: boolean
  /**
   * Analítica del carril (`view_item_list`/`select_item`/`add_to_cart` de la
   * card), con `analytics.items` alineado con `items`. Opcional: sin ella (tests)
   * el carril no emite nada.
   */
  readonly analytics?: ContextualProductListAnalytics
}

/**
 * Carril de tarjetas del módulo «ProductosBotonCompra»: en móvil/tablet es una
 * rejilla estática de 2 columnas (con `capOnMobile`, solo se ven las 4 primeras
 * tarjetas); desde escritorio (`@include breakpoint(desktop)`, ver
 * `ProductRail.module.scss`) pasa a un carril horizontal con `scroll-snap`
 * nativo y dos `ArrowBtn` superpuestas que avanzan/retroceden una tarjeta por
 * click.
 *
 * Todas las tarjetas se renderizan siempre en el DOM: el recorte a 4 en
 * móvil/tablet es puramente visual (CSS), igual que en la referencia, de modo
 * que un cambio de tamaño de ventana no exige volver a renderizar en cliente.
 * El botón "Ver más" solo quita esa clase de recorte — no vuelve a pedir ni a
 * montar tarjetas, ya están todas en el DOM.
 *
 * `'use client'` por las flechas y por el botón "Ver más": el resto del
 * módulo (`ProductosBotonCompra.tsx`) es Server Component — la interactividad
 * queda empujada a esta hoja (Constitución, Principio I). La mecánica de
 * scroll vive en `useRailScroll`, compartida con el carril de
 * `OtrosProductosIzq` (specs/031-productos-boton-compra-module/research.md §4).
 */
export function ProductRail({ items, capOnMobile = false, analytics }: ProductRailProps) {
  const { railRef, atStart, atEnd, recalc, scrollByStep } = useRailScroll(items.length)
  // Con ratón el carril no se arrastra de forma nativa (solo el gesto táctil).
  useDragScroll(railRef, items.length)
  const [expanded, setExpanded] = useState(false)
  const railId = useId()
  const { trackImpression, trackSelect, trackPurchase } = useContextualProductListTracking(analytics)

  // Con `capOnMobile`, hasta que se pulse "Ver más" el CSS oculta la 5ª
  // tarjeta en adelante en móvil/tablet (`.railCapped`, ver
  // `ProductRail.module.scss`). El botón es una acción pura de cliente: no
  // navega a ningún destino ni depende del enlace de cabecera del módulo.
  const isCapped = capOnMobile && !expanded

  return (
    <div className={styles.viewport}>
      {/* `data-testid`: única forma práctica de localizar el contenedor
          desplazable desde el test — no expone rol/nombre accesible propio
          (el carril no es una landmark, sus tarjetas ya son alcanzables
          individualmente por rol). */}
      <div
        ref={railRef}
        id={railId}
        className={cx(styles.rail, isCapped && styles.railCapped)}
        onScroll={recalc}
        data-testid="product-rail"
      >
        {items.map(({ id, ...card }, index) => {
          const base = analytics?.items[index]
          const cardElement = <VerticalCard {...card} />
          return (
            <div key={id} className={styles.item}>
              {base ? (
                <TrackedProductCard
                  index={index}
                  onImpression={() => trackImpression(`${index}:${base.item_id}`, withIndex(base, index))}
                  onSelect={() => trackSelect(withIndex(base, index))}
                  onPurchase={(href) => trackPurchase(withIndex(base, index), href)}
                >
                  {cardElement}
                </TrackedProductCard>
              ) : (
                cardElement
              )}
            </div>
          )
        })}
      </div>

      <div className={styles.nav}>
        {!atStart && (
          <ArrowBtn
            dir="left"
            overlay
            className={cx(styles.navBtn, styles.navBtnLeft)}
            onClick={() => scrollByStep(-1)}
          />
        )}
        {!atEnd && (
          <ArrowBtn
            dir="right"
            overlay
            className={cx(styles.navBtn, styles.navBtnRight)}
            onClick={() => scrollByStep(1)}
          />
        )}
      </div>

      {isCapped && (
        <div className={styles.moreWrap}>
          <button
            type="button"
            className={styles.moreBtn}
            aria-expanded={expanded}
            aria-controls={railId}
            onClick={() => setExpanded(true)}
          >
            Ver más
          </button>
        </div>
      )}
    </div>
  )
}
